import type {
  CardCondition,
  CardFinish,
  CardRarity,
} from "@/context/AppContext";

/**
 * Top-level intent buckets used by the Studio's intent-first navigation and
 * by user-defined bundle templates. Defined here (rather than in studio.tsx)
 * so the bundle-template engine can import it without pulling in the screen.
 */
export type StudioCategory =
  | "All"
  | "Random"
  | "Set"
  | "Theme"
  | "Decks"
  | "Trainer/Energy";

export const STUDIO_CATEGORIES: StudioCategory[] = [
  "All",
  "Random",
  "Set",
  "Theme",
  "Decks",
  "Trainer/Energy",
];

/** Categories the user can pick when authoring a bundle template (excludes "All"). */
export const TEMPLATE_INTENT_CHOICES: Exclude<StudioCategory, "All">[] = [
  "Random",
  "Set",
  "Theme",
  "Decks",
  "Trainer/Energy",
];

export type CardSupertype = "Pokemon" | "Trainer" | "Energy";

export interface BundleTemplateFilters {
  /** Whitelist of Card.setName. Empty/undefined = all sets. */
  sets?: string[];
  /** Whitelist of Card.rarity. */
  rarities?: CardRarity[];
  /** Whitelist of Card.finish (print treatment). Empty/undefined = any finish
   *  (including no-finish regular prints). Combined with `rarities` via OR
   *  semantics — a card matches if its rarity OR its finish is in the list. */
  finishes?: CardFinish[];
  /** Whitelist of Card.tcgSupertype (matched case-insensitively). */
  supertypes?: CardSupertype[];
  /** Whitelist of Card.tcgTypes (e.g. "Water", "Fire"). Matched against tcgTypes[0]. */
  types?: string[];
  /** Whitelist of Card.tcgSeries. */
  series?: string[];
  /** Card.game (e.g. "Pokemon", "Magic"). */
  game?: string;
  /** Allowed conditions; empty = any. */
  conditions?: CardCondition[];
  /** Optional max market price per card (USD). */
  maxPricePerCard?: number;
}

/**
 * How a template picks cards from the filtered pool to populate the bundle.
 *  - `top-value` (default): sort by marketPrice DESC, greedy fill. Maximizes
 *    bundle value per card but feels nothing like a mystery pack.
 *  - `bulk` / `standard` / `premium`: route through the weighted picker with
 *    the matching profile from `BASE_PROFILES` in features/bundleWeightedPicker.ts,
 *    layered with any user mix overrides from BundleSettings.
 *  - `shuffle`: filter, Fisher-Yates shuffle, fill until target qty. No
 *    rarity weighting — every eligible card is equally likely.
 */
export type BundleSelectionProfile =
  | "top-value"
  | "bulk"
  | "standard"
  | "premium"
  | "shuffle";

export const BUNDLE_SELECTION_PROFILES: BundleSelectionProfile[] = [
  "top-value",
  "bulk",
  "standard",
  "premium",
  "shuffle",
];

/** Human-readable label per profile for chip rows. */
export const BUNDLE_SELECTION_PROFILE_LABELS: Record<
  BundleSelectionProfile,
  string
> = {
  "top-value": "Top value",
  bulk: "Bulk mix",
  standard: "Standard",
  premium: "Premium",
  shuffle: "Shuffle",
};

/** One-line hint shown under the chip row to explain the active profile. */
export const BUNDLE_SELECTION_PROFILE_HINTS: Record<
  BundleSelectionProfile,
  string
> = {
  "top-value":
    "Picks the highest-priced cards first. Best for premium-margin packs.",
  bulk: "~70% commons, 22% uncommons, 6% rares, 2% holos. Edit weights in Tools → Bundle Config.",
  standard: "~50/25/15/8/2 mix with min rare + holo guarantees.",
  premium:
    "Hits-heavy: 30% rare, 40% holo, 20% ultra. Best for premium hit packs.",
  shuffle: "Randomized — every eligible card has an equal chance.",
};

export interface BundleSelection {
  profile: BundleSelectionProfile;
}

/**
 * User-authored recipe for a bundle. The Studio runs every saved template
 * against current inventory and surfaces matches under the chosen intent
 * with an availabilityPct (cardCount / targetCount).
 */
export interface BundleTemplate {
  id: string;
  name: string;
  intent: Exclude<StudioCategory, "All">;
  /** Total card qty for a 100% fill. */
  targetCount: number;
  filters: BundleTemplateFilters;
  /** How to pick from the filtered pool. Undefined = "top-value" for backward compat. */
  selection?: BundleSelection;
  createdAt: string;
}
